/**
 * Where a trainer was standing when the paywall opened.
 *
 * WHY THIS IS NOT THE EXISTING `reason` STRING
 *   openPaywall already took a `reason`, but it is a DISPLAY string: mostly
 *   prose ("Unlock variant tracking with Pro"), a few accidental slugs, and in
 *   three places a dynamic error message from the server. As an analytics
 *   dimension it is unusable — the same screen can produce different strings,
 *   and rewording a sentence would silently split one source into two.
 *
 *   So `source` is a separate, closed union. Adding a call site without
 *   choosing one is a type error, which is the only way a vocabulary like this
 *   stays clean once there are two dozen entry points.
 *
 * WHY IT CANNOT BE BACKFILLED
 *   Nothing has ever recorded this, so every purchase before it shipped is
 *   permanently unattributable. That is the argument for a closed union rather
 *   than free text: the data only starts existing once, and it should start
 *   clean.
 */
export const PAYWALL_SOURCES = [
  "main-menu",
  "settings",
  "settings-billing",
  "features-map",
  "power-scouter",
  "pvp-events",
  "event-dex-gaps",
  "profile-priority-widgets",
  "variant-trackers",
  "scan-history",
  "wish-list",
  "trade-matching",
  "trade-picks",
  /** The Trade search helper block on the Potential Trades pane. */
  "trade-search-helper",
  "friends-limit",
  "friends-pin",
  "friends-error",
  "notifications",
  "dex-restore",
  "qr-scan",
  "deep-link",
  /**
   * The shared ProLock card with no source of its own. Should stay near zero:
   * every ProLock call site passes the source that names it. See ProLock.tsx.
   */
  "pro-lock",
  /** The type-coverage podium on Meta: #1 is free, #2 and #3 are the tease. */
  "meta-podium",
  /** The detail-page scan upsell inside the Scan Pokedex modal. */
  "detail-scan",
  /** The full cross-trainer dex matrix on Friends. */
  "friends-matrix",
  /** The "Worth knowing" recommendation block on a My Pokémon record. */
  "my-pokemon-recs",
  /** The Raids-right-now "Hunting:" dex selector on Profile. */
  "raid-hunting",
  /** The Naming studio: copy-ready IV names and the hands-free session. */
  "naming",
  /** Box Cleanup: the transfer search strings and the removal flow. */
  "box-cleanup",
  /** The one-time Basic vs Pro comparison sheet (WhyProSheet). */
  "why-pro",
  /** Battle Hub, Yours: your best copies for each meta's top 100. */
  "meta-yours",
  /** Battle Hub: the magnifier that copies a ranked row's GO search. */
  "meta-search",
  /** Rocket page: this rotation's shadows against the trainer's Nexus Dex. */
  "rocket",
  /** Dynamax page: your best Max team per boss and your Max coverage. */
  "dynamax",
  /** A call site that has not chosen one yet. Should stay at zero. */
  "unknown",
] as const;

export type PaywallSource = (typeof PAYWALL_SOURCES)[number];

/** Human labels for the admin dashboard. */
export const PAYWALL_SOURCE_LABELS: Record<PaywallSource, string> = {
  "main-menu": "Main menu",
  settings: "Settings",
  "settings-billing": "Settings → Billing",
  "features-map": "Features map",
  "power-scouter": "Power Scouter",
  "pvp-events": "PvP events strip",
  "event-dex-gaps": "Event dex gaps",
  "profile-priority-widgets": "Profile priority widgets",
  "variant-trackers": "Variant trackers",
  "scan-history": "Scan history page",
  "wish-list": "Wish list",
  "trade-matching": "Trade matching",
  "trade-picks": "Live trade picks",
  "trade-search-helper": "Trade search helper",
  "friends-limit": "Friend limit reached",
  "friends-pin": "Pin a friend",
  "friends-error": "Friends (gated action)",
  notifications: "Notifications",
  "dex-restore": "Dex restore",
  "qr-scan": "QR scan (friend limit)",
  "deep-link": "Deep link (friend limit)",
  "pro-lock": "Pro lock (generic)",
  "meta-podium": "Meta podium (#2/#3)",
  "detail-scan": "Detail-page scan",
  "friends-matrix": "Full friends matrix",
  "my-pokemon-recs": "My Pokémon recommendations",
  "raid-hunting": "Raids Hunting dex selector",
  naming: "Naming studio",
  "box-cleanup": "Box Cleanup",
  "why-pro": "Why go Pro sheet",
  "meta-yours": "Battle Hub: your best in the meta",
  "meta-search": "Battle Hub: search string copy",
  rocket: "Rocket: dex gaps this rotation",
  dynamax: "Dynamax: best team and Max coverage",
  unknown: "Unattributed",
};

export function isPaywallSource(v: unknown): v is PaywallSource {
  return typeof v === "string" && (PAYWALL_SOURCES as readonly string[]).includes(v);
}

/**
 * Don't re-record the same source this often (migration 0071).
 *
 * A component can re-open the paywall on re-render, and a trainer poking the
 * same locked button three times in a row is one moment of interest, not three.
 * Without this the loudest source would be whichever screen re-renders most,
 * which is a UI detail rather than a signal about the product.
 *
 * Kept short so genuine repeat interest — hitting the same wall again an hour
 * later — still registers. That IS the signal.
 */
export const PAYWALL_OPEN_THROTTLE_MS = 5 * 60 * 1000;

const lastOpenAt = new Map<PaywallSource, number>();

/**
 * True when an open from this source should be recorded. Stateful by design:
 * the throttle is per tab, and a fresh session recording one open per source is
 * exactly right.
 */
export function shouldRecordOpen(
  source: PaywallSource,
  now: number = Date.now(),
  throttleMs: number = PAYWALL_OPEN_THROTTLE_MS,
): boolean {
  const prev = lastOpenAt.get(source);
  if (prev != null && now - prev < throttleMs) return false;
  lastOpenAt.set(source, now);
  return true;
}

/** Test seam — the throttle is module state. */
export function resetOpenThrottle(): void {
  lastOpenAt.clear();
}
